import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { IAssessmentProject, ICommentThread, IDam, IFailureMode, IProjectFailureMode, IProjectUser } from "../types/types";
import { get, post } from "../fetchers/fetchers";
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import { ArrowBack } from '@material-ui/icons';
import DialogContentText from '@material-ui/core/DialogContentText';

import {
    Button,
    Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    TableRow,
    TextField, TextFieldProps,
    Tooltip,
    withStyles
} from "@material-ui/core";
import Assessment from "./assessment";
import { useParams } from "react-router";
import ProjectFailureModes from "./project_failure_modes";
import Alert from "@material-ui/lab/Alert";

import { API_URL } from "../env";

const { REACT_APP_BRANCH } = process.env;

interface IAssessmentsProps {
}

interface IRouteParams {
    id: string
}



const AddingButton = withStyles(theme => ({
    root: {
        margin: "auto",
        backgroundColor: "rgba(0,103,114,0.2)",
        color: "rgba(0,103,114,1)",
        '&:hover': {
            backgroundColor: "rgba(0,103,114,0.5)",
        },
    },
}))(IconButton);


const Assessments: FC<IAssessmentsProps> = () => {

    const [assessments, setAssessments] = useState<IAssessmentProject[]>([]);
    const [dam, setDam] = useState<IDam>();
    const [reload, setReload] = useState<boolean>(true);
    const [damLoaded, setDamLoaded] = useState<boolean>(false);
    const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
    const newAssessmentName = useRef<TextFieldProps>(null);
    const params = useParams<IRouteParams>()
    const [damName, setDamName] = useState<string>("");
    const [projectAccessLevel, setProjectAccessLevel] = useState<number>(0);
    const [projectId, setProjectId] = useState<number>(0);

    const [newFailureModes, setNewFailureModes] = useState<IFailureMode[]>([]);

    const setFailureModes = useCallback((failureModes: IFailureMode[]) => {
        setNewFailureModes(failureModes)

    }, [setNewFailureModes]);

    const getAssessments = async (dam: IDam) => {
        let id = 0
        if (dam) {
            id = dam.id || 0
        }
        const d = await get<IAssessmentProject[]>(API_URL + "assessment_projects/" + id.toString());
        setAssessments(d)
    }

    const getProjectAccessLevel = async () => {
        if (projectId <= 0) {
            return
        }
        const d = await get<IProjectUser>(API_URL + "projects/" + projectId+ "/my_role");
        setProjectAccessLevel(d.access_level)
    }

    const getDam = async (): Promise<IDam> => {
        const d = await get<IDam>(API_URL + "dam/" + params.id)
        setDam(d);
        setProjectId(d.project_id || -1)
        setDamName(d.name);

        return d
    }

    const addAssessment = async (name: string): Promise<IAssessmentProject> => {
        let id = 0
        if (dam) {
            id = dam.id || 0
        }
        let d = await post<IAssessmentProject>(API_URL + "assessment_projects/" + id.toString(), { name: name, is_active: true });
        setReload(true)
        return d
    }

    const updateAssessment = async (assessment: IAssessmentProject): Promise<IAssessmentProject> => {
        let id = 0
        if (dam) {
            id = dam.id || 0
        }
        let d = await post<IAssessmentProject>(API_URL + "assessment_projects/" + id.toString(), { name: assessment.name, id: assessment.id, is_active: assessment.is_active });
        setReload(true)
        return d
    }

    const handleClose = () => {
        setOpenCreateDialog(false)
    }
    const handleOpen = () => {
        setOpenCreateDialog(true)
    }

    const handleBack = () => {
        window.history.back();
    }

    const addProjectFailureModes = async (assessment: IAssessmentProject) => {
        for (let i = 0; i < newFailureModes.length; i++) {
            if (newFailureModes[i].selected) {
                await post<IProjectFailureMode>(API_URL + "failure_modes/" + assessment.id, { failure_mode_id: newFailureModes[i].id });
            }
        }
    }

    const createAssessment = () => {
        let assessmentName = newAssessmentName.current?.value as string
        if (dam) {
            addAssessment(assessmentName).then(
                (assessment) => {
                    addProjectFailureModes(assessment).then(
                        (assessment) => {
                            setOpenCreateDialog(false)
                        }
                    )
                }
            )
        }
    }

    useEffect(() => {
        if (!damLoaded) {
            getDam().then((d) => { setDamLoaded(true); getAssessments(d).then(() => setReload(false)) })
        }

        if (reload && dam) {
            getAssessments(dam).then(() => setReload(false))
        }
    }, [reload, damLoaded, getAssessments, getDam])


    useEffect(() => {
        if (projectId > 0) {
            getProjectAccessLevel()
        }
    }, [projectId, getProjectAccessLevel])


    

    return (
        <Container>
            <Typography variant="h4" component="h1" gutterBottom>
                {damName}
            </Typography>
            {REACT_APP_BRANCH !== 'main' ? <Alert variant="filled" severity="warning">
                This is a development version, the data displayed may not be accurate and updates to the data will not be stored.
            </Alert> : null}

            <Dialog open={openCreateDialog} onClose={handleClose} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">Create Assessment</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Please enter the name of the new assessment.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Dam name"
                        type="text"
                        fullWidth
                        inputRef={newAssessmentName}
                    />
                    <ProjectFailureModes updateSelectedFailureModes={setFailureModes} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={createAssessment} color="primary">
                        Create
                    </Button>
                </DialogActions>
            </Dialog>


            <Grid container spacing={3} xl={12} direction="row" style={{ padding: "20px", marginTop: "40px" }}>
                <Grid item xs={5} sm={3} md={2} lg={2}>
                    <div style={{ position: "sticky", top: "68px" }}>
                        <Tooltip title="Back" placement={'bottom-end'}>
                            <AddingButton style={{ backgroundColor: "rgb(239,88,43)", color: "#FFFFFF" }} onClick={prevState => handleBack()} >
                                <ArrowBack />
                            </AddingButton>
                        </Tooltip>
                        <br />
                        { projectAccessLevel >=3 &&
                        <Tooltip title="Create new project" placement={'bottom-end'}>
                            <AddingButton style={{ backgroundColor: "rgb(239,88,43)", color: "#FFFFFF" }} onClick={prevState => handleOpen()} >
                                <AddIcon />
                            </AddingButton>
                        </Tooltip>
                        }
                    </div>
                </Grid>
                <Grid item xs={7} sm={9} md={9} lg={10} container spacing={3}>

                    {assessments.filter(a => a.is_active).map(d => {
                        return (<Assessment assessment={d} updateAssessment={updateAssessment} accessLevel={projectAccessLevel} />)
                    })}

                </Grid>
            </Grid>
        </Container>
    );
};

export default Assessments;

